import { readApiError } from '@shared/api/errors/read-api-error';
import type { SecondFactor, SecondFactorMethod } from '@shared/api/contracts/security';

export interface SecondFactorAsk {
  purpose: string;
  methods: SecondFactorMethod[];
  invalid?: boolean;
  sendEmail?: () => Promise<void>;
}

export type AskSecondFactor = (input: SecondFactorAsk) => Promise<SecondFactor | null>;

const asMethods = (values: string[] | undefined, fallback: SecondFactorMethod[]): SecondFactorMethod[] => {
  const allowed = (values ?? []).filter(
    (item): item is SecondFactorMethod => item === 'totp' || item === 'email' || item === 'backup',
  );
  return allowed.length > 0 ? allowed : fallback;
};

export async function runSecondFactorAction<T>(
  ask: AskSecondFactor,
  options: {
    purpose: string;
    methods?: SecondFactorMethod[];
    required?: boolean;
    sendEmail?: () => Promise<void>;
    request: (factor?: SecondFactor) => Promise<T>;
  },
): Promise<
  | { ok: true; data: T }
  | { ok: false; cancelled: true; error?: unknown }
  | { ok: false; cancelled?: false; error: unknown }
> {
  const fallback = options.methods ?? ['totp', 'backup'];
  let factor: SecondFactor | undefined;

  if (options.required) {
    const asked = await ask({ purpose: options.purpose, methods: fallback, sendEmail: options.sendEmail });
    if (!asked) {
      return { ok: false, cancelled: true };
    }
    factor = asked;
  }

  for (let attempt = 0; attempt < 5; attempt += 1) {
    try {
      const data = await options.request(factor);
      return { ok: true, data };
    } catch (error) {
      const parsed = readApiError(error);
      if (parsed?.code === 'OTP_REQUIRED' || parsed?.code === 'OTP_INVALID') {
        const asked = await ask({
          purpose: parsed.purpose ?? options.purpose,
          methods: asMethods(parsed.methods, fallback),
          invalid: parsed.code === 'OTP_INVALID',
          sendEmail: options.sendEmail,
        });
        if (!asked) {
          return { ok: false, cancelled: true, error };
        }
        factor = asked;
        continue;
      }
      return { ok: false, error };
    }
  }

  return { ok: false, error: new Error('second factor attempts exceeded') };
}
